import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { isTransitionalStatus, LIST_POLL_MS } from "@/lib/polling";
import {
  getGatewayOAuth,
  setGatewayOAuth,
  revokeOAuthGrant,
  removeOAuthClient,
  getOAuthConsentRequest,
  getGatewayConfig,
  deleteBudget,
  listBudgets,
  putBudget,
  createAgent,
  createCredential,
  createGateway,
  createProvider,
  createToolServer,
  deleteAgent,
  deleteGateway,
  deleteProvider,
  deleteToolServer,
  discoverTools,
  getAgentBuildLog,
  getAgentLogs,
  getClientSnippets,
  getGatewayFlow,
  getGateway,
  getGatewayActivity,
  getGatewayLogs,
  listRoles,
  createRole,
  updateRole,
  resetRole,
  deleteRole,
  setCredentialRole,
  listToolTiers,
  setToolTiers,
  getGatewaySecurity,
  getGatewayTraces,
  getPlaygroundTargets,
  previewOpenApi,
  refreshOpenApiSpec,
  removeTelemetryExport,
  runPlayground,
  setTelemetryExport,
  getTelemetryExport,
  getAdvancedView,
  openAdvancedView,
  listAgentEndpoints,
  createAgentEndpoint,
  updateAgentEndpoint,
  deleteAgentEndpoint,
  discoverAgentEndpoint,
  listPublishedAgents,
  publishAgent,
  getGatewayMetrics,
  getModelAliases,
  getPolicy,
  listAgentBuilds,
  listAgents,
  listCatalog,
  listCredentials,
  listGateways,
  listPlaneClasses,
  listProviders,
  listToolServers,
  revokeCredential,
  setModelAliases,
  setPolicy,
  startAgent,
  stopAgent,
  isolateAgent,
  pauseAgent,
  releaseAgent,
  resumeAgent,
  updateAgent,
  updateGateway,
  updateGatewaySoftware,
  resizeGateway,
  updateProvider,
  updateToolServer,
  createDelegation,
  listDelegations,
  listToolServerDelegations,
  revokeDelegation,
  setToolServerDelegation,
  listContentRuleSets,
  listContentRules,
  createContentRule,
  updateContentRule,
  deleteContentRule,
  reissueInstallToken,
} from "./api";
import type {
  FlowView,
  GatewayOAuth,
  Budget,
  BudgetPutRequest,
  GatewaySecurity,
  GatewaySecurityQuery,
  Agent,
  AgentCreateRequest,
  AgentUpdateRequest,
  CatalogEntry,
  CredentialCreated,
  CredentialCreateRequest,
  Gateway,
  GatewaySize,
  GatewayCreateRequest,
  ModelAliases,
  GatewayActivity,
  GatewayActivityQuery,
  GatewayLogsQuery,
  Credential,
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  ServerTiers,
  Tier,
  GatewayLogsResponse,
  GatewayTracesQuery,
  OpenApiPreview,
  OpenApiPreviewRequest,
  TelemetryExport,
  TelemetryExportRequest,
  AdvancedViewSession,
  PlaygroundRunRequest,
  PlaygroundRunResult,
  GatewayTracesResponse,
  AgentEndpoint,
  AgentEndpointCreateRequest,
  AgentEndpointUpdateRequest,
  PublishedAgent,
  PublishRequest,
  GatewayMetrics,
  GatewayUpdateRequest,
  Provider,
  ProviderCreateRequest,
  ProviderUpdateRequest,
  ToolPolicy,
  ToolServer,
  ToolServerCreateRequest,
  ToolServerUpdateRequest,
  DelegationCreated,
  DelegationCreateRequest,
  ToolServerDelegation,
  ContentRule,
  ContentRuleCreateRequest,
  ContentRuleSet,
  ContentRuleUpdateRequest,
  GatewayInstall,
} from "./types";
import { asGatewayFlow } from "./flowAnswer";

export const agentGatewayKeys = {
  all: ["agent-gateways"] as const,
  list: () => ["agent-gateways", "list"] as const,
  detail: (id: string) => ["agent-gateways", "detail", id] as const,
  planeClasses: () => ["agent-plane", "classes"] as const,
  snippets: (id: string) => ["agent-gateways", "detail", id, "snippets"] as const,
  toolServers: (id: string) => ["agent-gateways", "detail", id, "tool-servers"] as const,
  providers: (id: string) => ["agent-gateways", "detail", id, "providers"] as const,
  credentials: (id: string) => ["agent-gateways", "detail", id, "credentials"] as const,
  roles: (id: string) => ["agent-gateways", "detail", id, "roles"] as const,
  toolTiers: (id: string) => ["agent-gateways", "detail", id, "tool-tiers"] as const,
  policy: (id: string, credentialId: string) =>
    ["agent-gateways", "detail", id, "credentials", credentialId, "policy"] as const,
  delegatedToolServers: (id: string) =>
    ["agent-gateways", "detail", id, "delegated-tool-servers"] as const,
  delegations: (id: string) => ["agent-gateways", "detail", id, "delegations"] as const,
  modelAliases: (id: string) => ["agent-gateways", "detail", id, "model-aliases"] as const,
  catalog: () => ["agent-gateways", "catalog"] as const,
  agents: (id: string) => ["agent-gateways", "detail", id, "agents"] as const,
  agentLogs: (id: string, agentId: string) =>
    ["agent-gateways", "detail", id, "agents", agentId, "logs"] as const,
  agentBuilds: (id: string, agentId: string) =>
    ["agent-gateways", "detail", id, "agents", agentId, "builds"] as const,
  agentBuildLog: (id: string, agentId: string, buildId: string) =>
    ["agent-gateways", "detail", id, "agents", agentId, "builds", buildId, "log"] as const,
  metrics: (id: string, hours: number) =>
    ["agent-gateways", "detail", id, "metrics", hours] as const,
  activity: (id: string, query: GatewayActivityQuery) =>
    ["agent-gateways", "detail", id, "activity", query] as const,
  logs: (id: string, query: GatewayLogsQuery) =>
    ["agent-gateways", "detail", id, "logs", query] as const,
  traces: (id: string, query: GatewayTracesQuery) =>
    ["agent-gateways", "detail", id, "traces", query] as const,
  security: (id: string, query: GatewaySecurityQuery) =>
    ["agent-gateways", "detail", id, "security", query] as const,
  agentEndpoints: (id: string) => ["agent-gateways", "detail", id, "agent-endpoints"] as const,
  publishedAgents: (id: string) => ["agent-gateways", "detail", id, "published-agents"] as const,
  contentRules: (id: string) => ["agent-gateways", "detail", id, "content-rules"] as const,
  contentRuleSets: () => ["agent-gateways", "content-rule-sets"] as const,
};

/** Agents report through the gateway's own cycle (about every 15 s). */
const AGENT_POLL_MS = 10_000;

/** A gateway whose desired generation has not been applied yet is still
 *  pushing; keep the row fresh until the two agree. */
function gatewayInFlight(g: Gateway): boolean {
  return (
    isTransitionalStatus(g.status) ||
    (g.status === "active" && g.generation_applied !== g.generation)
  );
}

export function useGateways() {
  return useQuery({
    queryKey: agentGatewayKeys.list(),
    queryFn: listGateways,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(gatewayInFlight) ? LIST_POLL_MS : false,
  });
}

export function useGateway(gatewayId: string | undefined) {
  return useQuery<Gateway>({
    queryKey: agentGatewayKeys.detail(gatewayId ?? ""),
    queryFn: () => getGateway(gatewayId as string),
    enabled: Boolean(gatewayId),
    refetchInterval: (query) =>
      query.state.data && gatewayInFlight(query.state.data) ? LIST_POLL_MS : false,
  });
}

/** The security plane's class list (AGW-22); only asked for when the plane is on. */
export function usePlaneClasses(enabled: boolean) {
  return useQuery({
    queryKey: agentGatewayKeys.planeClasses(),
    // deferred so a page with the plane off never touches the call (or its mock)
    queryFn: () => listPlaneClasses(),
    enabled,
    staleTime: 60_000,
  });
}

export function useClientSnippets(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.snippets(gatewayId ?? ""),
    queryFn: () => getClientSnippets(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

function useInvalidateGateway(gatewayId?: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: agentGatewayKeys.list() });
    if (gatewayId) {
      void queryClient.invalidateQueries({
        queryKey: agentGatewayKeys.detail(gatewayId),
      });
    }
  };
}

export function useCreateGateway(): UseMutationResult<Gateway, Error, GatewayCreateRequest> {
  const invalidate = useInvalidateGateway();
  return useMutation({ mutationFn: createGateway, onSuccess: invalidate });
}

export function useUpdateGateway(
  gatewayId: string,
): UseMutationResult<Gateway, Error, GatewayUpdateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => updateGateway(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateGatewaySoftware(
  gatewayId: string,
): UseMutationResult<Gateway, Error, void> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({ mutationFn: () => updateGatewaySoftware(gatewayId), onSuccess: invalidate });
}

export function useResizeGateway(
  gatewayId: string,
): UseMutationResult<Gateway, Error, GatewaySize> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (size) => resizeGateway(gatewayId, size),
    onSuccess: invalidate,
  });
}

export function useDeleteGateway(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway();
  return useMutation({ mutationFn: deleteGateway, onSuccess: invalidate });
}

// --- tool servers ---

export function useToolServers(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.toolServers(gatewayId ?? ""),
    queryFn: () => listToolServers(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateToolServer(
  gatewayId: string,
): UseMutationResult<ToolServer, Error, ToolServerCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createToolServer(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateToolServer(
  gatewayId: string,
): UseMutationResult<ToolServer, Error, { serverId: string; payload: ToolServerUpdateRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ serverId, payload }) => updateToolServer(gatewayId, serverId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteToolServer(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (serverId) => deleteToolServer(gatewayId, serverId),
    onSuccess: invalidate,
  });
}

export function useDiscoverTools(gatewayId: string): UseMutationResult<ToolServer, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (serverId) => discoverTools(gatewayId, serverId),
    onSuccess: invalidate,
  });
}

// --- model providers ---

export function useProviders(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.providers(gatewayId ?? ""),
    queryFn: () => listProviders(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateProvider(
  gatewayId: string,
): UseMutationResult<Provider, Error, ProviderCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createProvider(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateProvider(
  gatewayId: string,
): UseMutationResult<Provider, Error, { providerId: string; payload: ProviderUpdateRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ providerId, payload }) => updateProvider(gatewayId, providerId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteProvider(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (providerId) => deleteProvider(gatewayId, providerId),
    onSuccess: invalidate,
  });
}

// --- agent credentials + policy ---

export function useCredentials(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.credentials(gatewayId ?? ""),
    queryFn: () => listCredentials(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateCredential(
  gatewayId: string,
): UseMutationResult<CredentialCreated, Error, CredentialCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createCredential(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useRevokeCredential(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (credentialId) => revokeCredential(gatewayId, credentialId),
    onSuccess: invalidate,
  });
}

// --- roles as saved policies (AGW-44) ---

export function useRoles(gatewayId: string | undefined) {
  return useQuery<Role[], Error>({
    queryKey: agentGatewayKeys.roles(gatewayId ?? ""),
    queryFn: () => listRoles(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useToolTiers(gatewayId: string | undefined) {
  return useQuery<ServerTiers[], Error>({
    queryKey: agentGatewayKeys.toolTiers(gatewayId ?? ""),
    queryFn: () => listToolTiers(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateRole(
  gatewayId: string,
): UseMutationResult<Role, Error, RoleCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createRole(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateRole(
  gatewayId: string,
): UseMutationResult<Role, Error, { roleId: string; payload: RoleUpdateRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ roleId, payload }) => updateRole(gatewayId, roleId, payload),
    onSuccess: invalidate,
  });
}

export function useResetRole(gatewayId: string): UseMutationResult<Role, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (roleId) => resetRole(gatewayId, roleId),
    onSuccess: invalidate,
  });
}

export function useDeleteRole(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (roleId) => deleteRole(gatewayId, roleId),
    onSuccess: invalidate,
  });
}

export function useSetCredentialRole(
  gatewayId: string,
): UseMutationResult<Credential, Error, { credentialId: string; roleId: string | null }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ credentialId, roleId }) => setCredentialRole(gatewayId, credentialId, roleId),
    onSuccess: invalidate,
  });
}

export function useSetToolTiers(
  gatewayId: string,
): UseMutationResult<ServerTiers, Error, { serverId: string; tiers: Record<string, Tier | null> }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ serverId, tiers }) => setToolTiers(gatewayId, serverId, tiers),
    onSuccess: invalidate,
  });
}

// --- delegation (plan AGW-8) ---

export function useToolServerDelegations(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.delegatedToolServers(gatewayId ?? ""),
    queryFn: () => listToolServerDelegations(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useSetToolServerDelegation(
  gatewayId: string,
): UseMutationResult<ToolServerDelegation, Error, { toolServerId: string; enabled: boolean }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ toolServerId, enabled }) =>
      setToolServerDelegation(gatewayId, toolServerId, enabled),
    onSuccess: invalidate,
  });
}

export function useDelegations(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.delegations(gatewayId ?? ""),
    queryFn: () => listDelegations(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateDelegation(
  gatewayId: string,
): UseMutationResult<DelegationCreated, Error, DelegationCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createDelegation(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useRevokeDelegation(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (delegationId) => revokeDelegation(gatewayId, delegationId),
    onSuccess: invalidate,
  });
}

export function usePolicy(gatewayId: string | undefined, credentialId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.policy(gatewayId ?? "", credentialId ?? ""),
    queryFn: () => getPolicy(gatewayId as string, credentialId as string),
    enabled: Boolean(gatewayId && credentialId),
  });
}

export function useSetPolicy(
  gatewayId: string,
): UseMutationResult<ToolPolicy, Error, { credentialId: string; payload: ToolPolicy }> {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ credentialId, payload }) => setPolicy(gatewayId, credentialId, payload),
    onSuccess: (data, { credentialId }) => {
      queryClient.setQueryData(agentGatewayKeys.policy(gatewayId, credentialId), data);
      invalidate();
    },
  });
}

// --- agents ---

export function useAgents(gatewayId: string | undefined) {
  return useQuery({
    queryKey: agentGatewayKeys.agents(gatewayId ?? ""),
    queryFn: () => listAgents(gatewayId as string),
    enabled: Boolean(gatewayId),
    // State comes from the gateway's periodic report, so a start/stop shows
    // up a cycle later: poll while any agent is not where it was asked to be.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (a) =>
          (a.desired === "running" && a.state !== "running") ||
          (a.desired === "stopped" && a.state === "running") ||
          a.latest_build?.status === "queued" ||
          a.latest_build?.status === "building",
      )
        ? AGENT_POLL_MS
        : false,
  });
}

export function useCreateAgent(
  gatewayId: string,
): UseMutationResult<Agent, Error, AgentCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createAgent(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateAgent(
  gatewayId: string,
): UseMutationResult<Agent, Error, { agentId: string; payload: AgentUpdateRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ agentId, payload }) => updateAgent(gatewayId, agentId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteAgent(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => deleteAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

export function useStartAgent(gatewayId: string): UseMutationResult<Agent, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => startAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

export function useStopAgent(gatewayId: string): UseMutationResult<Agent, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => stopAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

export function usePauseAgent(gatewayId: string): UseMutationResult<Agent, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => pauseAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

// --- the Security tab and Isolate (AGW-28) ---

/** The security plane's records for the cell, refreshed every 10 s while followed:
 *  a denial reaches the tab within the plan's 15 s. */
export function useGatewaySecurity(
  gatewayId: string,
  query: GatewaySecurityQuery,
  follow: boolean,
) {
  return useQuery<GatewaySecurity>({
    queryKey: agentGatewayKeys.security(gatewayId, query),
    queryFn: () => getGatewaySecurity(gatewayId, query),
    refetchInterval: follow ? 10_000 : false,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useIsolateAgent(
  gatewayId: string,
): UseMutationResult<Agent, Error, { agentId: string; reason?: string }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ agentId, reason }) => isolateAgent(gatewayId, agentId, reason),
    onSuccess: invalidate,
  });
}

export function useReleaseAgent(gatewayId: string): UseMutationResult<Agent, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => releaseAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

export function useResumeAgent(gatewayId: string): UseMutationResult<Agent, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (agentId) => resumeAgent(gatewayId, agentId),
    onSuccess: invalidate,
  });
}

export function useAgentLogs(gatewayId: string, agentId: string | null) {
  return useQuery({
    queryKey: agentGatewayKeys.agentLogs(gatewayId, agentId ?? "none"),
    queryFn: () => getAgentLogs(gatewayId, agentId as string),
    enabled: agentId !== null,
    refetchInterval: AGENT_POLL_MS,
  });
}

// --- builds from a zip (AGW-12) ---

export function useAgentBuilds(gatewayId: string, agentId: string | null) {
  return useQuery({
    queryKey: agentGatewayKeys.agentBuilds(gatewayId, agentId ?? "none"),
    queryFn: () => listAgentBuilds(gatewayId, agentId as string),
    enabled: agentId !== null,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status === "queued" || b.status === "building")
        ? AGENT_POLL_MS
        : false,
  });
}

export function useAgentBuildLog(
  gatewayId: string,
  agentId: string | null,
  buildId: string | null,
) {
  return useQuery({
    queryKey: agentGatewayKeys.agentBuildLog(gatewayId, agentId ?? "none", buildId ?? "none"),
    queryFn: () => getAgentBuildLog(gatewayId, agentId as string, buildId as string),
    enabled: agentId !== null && buildId !== null,
    refetchInterval: (query) =>
      query.state.data &&
      query.state.data.status !== "succeeded" &&
      query.state.data.status !== "failed"
        ? AGENT_POLL_MS
        : false,
  });
}

// --- metrics and logs (AGW-7) ---

export function useGatewayMetrics(gatewayId: string, hours: number) {
  return useQuery<GatewayMetrics>({
    queryKey: agentGatewayKeys.metrics(gatewayId, hours),
    queryFn: () => getGatewayMetrics(gatewayId, hours),
    refetchInterval: 60_000,
  });
}

/** Keeps the previous rows on screen while a filter change is in flight. */
export function useGatewayActivity(
  gatewayId: string,
  query: GatewayActivityQuery,
  follow: boolean,
) {
  return useQuery<GatewayActivity>({
    queryKey: agentGatewayKeys.activity(gatewayId, query),
    queryFn: () => getGatewayActivity(gatewayId, query),
    refetchInterval: follow ? 10_000 : false,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useGatewayLogs(gatewayId: string, query: GatewayLogsQuery, follow: boolean) {
  return useQuery<GatewayLogsResponse>({
    queryKey: agentGatewayKeys.logs(gatewayId, query),
    queryFn: () => getGatewayLogs(gatewayId, query),
    refetchInterval: follow ? 10_000 : false,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

// --- agent endpoints and published agents (AGW-15) ---

export function useAgentEndpoints(gatewayId: string | undefined) {
  return useQuery<AgentEndpoint[], Error>({
    queryKey: agentGatewayKeys.agentEndpoints(gatewayId ?? ""),
    queryFn: () => listAgentEndpoints(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateAgentEndpoint(
  gatewayId: string,
): UseMutationResult<AgentEndpoint, Error, AgentEndpointCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createAgentEndpoint(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateAgentEndpoint(
  gatewayId: string,
): UseMutationResult<
  AgentEndpoint,
  Error,
  { endpointId: string; payload: AgentEndpointUpdateRequest }
> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ endpointId, payload }) => updateAgentEndpoint(gatewayId, endpointId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteAgentEndpoint(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (endpointId) => deleteAgentEndpoint(gatewayId, endpointId),
    onSuccess: invalidate,
  });
}

export function useDiscoverAgentEndpoint(
  gatewayId: string,
): UseMutationResult<AgentEndpoint, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (endpointId) => discoverAgentEndpoint(gatewayId, endpointId),
    onSuccess: invalidate,
  });
}

export function usePublishedAgents(gatewayId: string | undefined) {
  return useQuery<PublishedAgent[], Error>({
    queryKey: agentGatewayKeys.publishedAgents(gatewayId ?? ""),
    queryFn: () => listPublishedAgents(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function usePublishAgent(
  gatewayId: string,
): UseMutationResult<PublishedAgent, Error, { agentId: string; payload: PublishRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ agentId, payload }) => publishAgent(gatewayId, agentId, payload),
    onSuccess: invalidate,
  });
}

// --- traces (AGW-13) ---

/** Keeps the previous rows on screen while a filter change is in flight. */
export function useGatewayTraces(
  gatewayId: string,
  query: GatewayTracesQuery,
  follow: boolean,
  enabled = true,
) {
  return useQuery<GatewayTracesResponse>({
    queryKey: agentGatewayKeys.traces(gatewayId, query),
    queryFn: () => getGatewayTraces(gatewayId, query),
    refetchInterval: follow ? 10_000 : false,
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });
}

// --- the catalog and model aliases (AGW-11) ---

/** Starters proven on this cloud; the list changes only when the catalog file does. */
export function useCatalog() {
  return useQuery<CatalogEntry[], Error>({
    queryKey: agentGatewayKeys.catalog(),
    queryFn: listCatalog,
    staleTime: 5 * 60_000,
  });
}

export function useModelAliases(gatewayId: string | undefined) {
  return useQuery<ModelAliases, Error>({
    queryKey: agentGatewayKeys.modelAliases(gatewayId ?? ""),
    queryFn: () => getModelAliases(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useSetModelAliases(
  gatewayId: string,
): UseMutationResult<ModelAliases, Error, ModelAliases> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => setModelAliases(gatewayId, payload),
    onSuccess: (data) => {
      qc.setQueryData(agentGatewayKeys.modelAliases(gatewayId), data);
      void qc.invalidateQueries({ queryKey: agentGatewayKeys.detail(gatewayId) });
    },
  });
}

// ---- Content rules (AGW-16) --------------------------------------------------------------
export function useContentRuleSets() {
  return useQuery<ContentRuleSet[], Error>({
    queryKey: agentGatewayKeys.contentRuleSets(),
    queryFn: listContentRuleSets,
    staleTime: 10 * 60 * 1000,
  });
}

export function useContentRules(gatewayId: string | undefined) {
  return useQuery<ContentRule[], Error>({
    queryKey: agentGatewayKeys.contentRules(gatewayId ?? ""),
    queryFn: () => listContentRules(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useCreateContentRule(
  gatewayId: string,
): UseMutationResult<ContentRule, Error, ContentRuleCreateRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => createContentRule(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateContentRule(
  gatewayId: string,
): UseMutationResult<ContentRule, Error, { ruleId: string; payload: ContentRuleUpdateRequest }> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: ({ ruleId, payload }) => updateContentRule(gatewayId, ruleId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteContentRule(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (ruleId) => deleteContentRule(gatewayId, ruleId),
    onSuccess: invalidate,
  });
}

// ---- Off-cloud gateways (AGW-19) --------------------------------------------------------
export function useReissueInstallToken(
  gatewayId: string,
): UseMutationResult<GatewayInstall, Error, void> {
  return useMutation({ mutationFn: () => reissueInstallToken(gatewayId) });
}

// --- playground (Phase 5 AGW-37) ---

export const playgroundKeys = {
  targets: (id: string) => ["agent-gateways", "detail", id, "playground"] as const,
};

/** What the Playground can run; polled while the gateway is not ready to take a call. */
export function usePlaygroundTargets(gatewayId: string | undefined) {
  return useQuery({
    queryKey: playgroundKeys.targets(gatewayId ?? ""),
    queryFn: () => getPlaygroundTargets(gatewayId as string),
    enabled: Boolean(gatewayId),
    refetchInterval: (query) =>
      query.state.data && !query.state.data.ready ? AGENT_POLL_MS : false,
  });
}

export function useRunPlayground(
  gatewayId: string,
): UseMutationResult<PlaygroundRunResult, Error, PlaygroundRunRequest> {
  return useMutation({ mutationFn: (payload) => runPlayground(gatewayId, payload) });
}

// --- OpenAPI to tools (Phase 5 AGW-40) ---

export function usePreviewOpenApi(
  gatewayId: string,
): UseMutationResult<OpenApiPreview, Error, OpenApiPreviewRequest> {
  return useMutation({ mutationFn: (payload) => previewOpenApi(gatewayId, payload) });
}

export function useRefreshOpenApiSpec(
  gatewayId: string,
): UseMutationResult<ToolServer, Error, string> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (serverId) => refreshOpenApiSpec(gatewayId, serverId),
    onSuccess: invalidate,
  });
}

// --- telemetry export (Phase 5 AGW-42) ---

export const telemetryKeys = {
  export: (id: string) => ["agent-gateways", "detail", id, "telemetry"] as const,
};

export function useTelemetryExport(gatewayId: string | undefined) {
  return useQuery({
    queryKey: telemetryKeys.export(gatewayId ?? ""),
    queryFn: () => getTelemetryExport(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

export function useSetTelemetryExport(
  gatewayId: string,
): UseMutationResult<TelemetryExport, Error, TelemetryExportRequest> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({
    mutationFn: (payload) => setTelemetryExport(gatewayId, payload),
    onSuccess: invalidate,
  });
}

export function useRemoveTelemetryExport(
  gatewayId: string,
): UseMutationResult<TelemetryExport, Error, void> {
  const invalidate = useInvalidateGateway(gatewayId);
  return useMutation({ mutationFn: () => removeTelemetryExport(gatewayId), onSuccess: invalidate });
}

// --- budgets (Phase 5 AGW-43) ---

export const budgetKeys = {
  list: (id: string) => ["agent-gateways", "detail", id, "budgets"] as const,
};

/** The gateway's budgets with what was spent; read again each minute (the product counts every few). */
export function useBudgets(gatewayId: string | undefined) {
  return useQuery({
    queryKey: budgetKeys.list(gatewayId ?? ""),
    queryFn: () => listBudgets(gatewayId as string),
    enabled: Boolean(gatewayId),
    refetchInterval: 60_000,
  });
}

export function usePutBudget(
  gatewayId: string,
): UseMutationResult<Budget, Error, BudgetPutRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => putBudget(gatewayId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: budgetKeys.list(gatewayId) });
      void queryClient.invalidateQueries({ queryKey: agentGatewayKeys.detail(gatewayId) });
    },
  });
}

export function useDeleteBudget(gatewayId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (budgetId) => deleteBudget(gatewayId, budgetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: budgetKeys.list(gatewayId) });
      void queryClient.invalidateQueries({ queryKey: agentGatewayKeys.detail(gatewayId) });
    },
  });
}

// --- the running configuration (Phase 5 AGW-39) ---

export const gatewayConfigKeys = {
  config: (id: string) => ["agent-gateways", "detail", id, "config"] as const,
};

/** The redacted configuration; read again every few seconds while a change is on its way. */
export function useGatewayConfig(gatewayId: string | undefined) {
  return useQuery({
    queryKey: gatewayConfigKeys.config(gatewayId ?? ""),
    queryFn: () => getGatewayConfig(gatewayId as string),
    enabled: Boolean(gatewayId),
    refetchInterval: (query) => (query.state.data?.pending ? LIST_POLL_MS : false),
  });
}

// --- MCP OAuth 2.1 (Phase 5 AGW-47) ---

export const oauthKeys = {
  gateway: (id: string) => ["agent-gateways", "detail", id, "oauth"] as const,
  request: (id: string) => ["agent-gateways", "oauth-request", id] as const,
};

export function useGatewayOAuth(gatewayId: string | undefined) {
  return useQuery({
    queryKey: oauthKeys.gateway(gatewayId ?? ""),
    queryFn: () => getGatewayOAuth(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

// --- the gateway's own view, read-only (Phase 5 AGW-45) ---

export function useAdvancedView(gatewayId: string | undefined) {
  return useQuery({
    queryKey: ["agent-gateways", "detail", gatewayId ?? "", "advanced-view"] as const,
    queryFn: () => getAdvancedView(gatewayId as string),
    enabled: Boolean(gatewayId),
  });
}

function useOAuthInvalidate(gatewayId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: oauthKeys.gateway(gatewayId) });
    void queryClient.invalidateQueries({ queryKey: agentGatewayKeys.detail(gatewayId) });
  };
}

export function useSetGatewayOAuth(
  gatewayId: string,
): UseMutationResult<GatewayOAuth, Error, boolean> {
  const invalidate = useOAuthInvalidate(gatewayId);
  return useMutation({
    mutationFn: (enabled) => setGatewayOAuth(gatewayId, enabled),
    onSuccess: invalidate,
  });
}

export function useRevokeOAuthGrant(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useOAuthInvalidate(gatewayId);
  return useMutation({
    mutationFn: (grantId) => revokeOAuthGrant(gatewayId, grantId),
    onSuccess: invalidate,
  });
}

export function useRemoveOAuthClient(gatewayId: string): UseMutationResult<void, Error, string> {
  const invalidate = useOAuthInvalidate(gatewayId);
  return useMutation({
    mutationFn: (clientId) => removeOAuthClient(gatewayId, clientId),
    onSuccess: invalidate,
  });
}

export function useOAuthConsentRequest(requestId: string | undefined) {
  return useQuery({
    queryKey: oauthKeys.request(requestId ?? ""),
    queryFn: () => getOAuthConsentRequest(requestId as string),
    enabled: Boolean(requestId),
    retry: false,
  });
}

export function useOpenAdvancedView(
  gatewayId: string,
): UseMutationResult<AdvancedViewSession, Error, void> {
  return useMutation({ mutationFn: () => openAdvancedView(gatewayId) });
}

/** The flow view (AGW-49, AGW-51). Its key lives under the gateway's, so every change that
 *  re-renders the gateway refreshes the configured view; the observed one refreshes itself
 *  every 15 s while the 1-hour window is on screen. */
export function useGatewayFlow(
  gatewayId: string | undefined,
  params: { view: FlowView; hours?: number; ownChecks?: boolean },
  options: { live?: boolean } = {},
) {
  const hours = params.hours ?? 24;
  const ownChecks = params.ownChecks ?? false;
  return useQuery({
    queryKey: [...agentGatewayKeys.detail(gatewayId ?? ""), "flow", params.view, hours, ownChecks],
    // an answer that is not the flow is a failed read, never a render that throws (flowAnswer.ts)
    queryFn: async () =>
      asGatewayFlow(await getGatewayFlow(gatewayId as string, { view: params.view, hours, ownChecks })),
    enabled: Boolean(gatewayId),
    placeholderData: keepPreviousData,
    refetchInterval: options.live ? 15_000 : false,
  });
}

